import { useCallback, useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import { format, parseISO } from 'date-fns';
import { Helmet } from 'react-helmet';
import Markdown from 'react-markdown';
import remarkDirective from 'remark-directive';
import { useParams } from 'react-router-dom';

import { AsyncPage } from '@/components';
import { createUseThemedStyles } from '@/hooks';
import { AiConversation, ReportAiConversationMetadata } from '@/lib/models';
import { aiConversationService } from '@/lib/services';
import { getRemarkComponents, remarkCustomTokens } from '@/lib/utils/markdown-utils';

const useStyles = createUseThemedStyles((theme) => ({
	page: {
		minHeight: 'calc(100vh - 60px)',
		paddingTop: 32,
		paddingBottom: 48,
		backgroundColor: theme.colors.gray100,
	},
	header: {
		maxWidth: 768,
		margin: '0 auto 32px',
	},
	title: {
		marginBottom: 12,
	},
	subtitle: {
		marginBottom: 0,
		color: theme.colors.gray700,
	},
	metadataCard: {
		maxWidth: 768,
		margin: '0 auto 24px',
		padding: 20,
		borderRadius: 5,
		backgroundColor: theme.colors.white,
		border: `1px solid ${theme.colors.gray300}`,
	},
	metadataRow: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: 24,
	},
	metadataItem: {
		minWidth: 180,
	},
	metadataLabel: {
		marginBottom: 6,
		fontSize: 12,
		fontWeight: 700,
		letterSpacing: '0.04em',
		textTransform: 'uppercase',
		color: theme.colors.gray700,
	},
	metadataValue: {
		marginBottom: 0,
	},
	conversation: {
		maxWidth: 768,
		margin: '0 auto',
	},
	queryBlock: {
		marginBottom: 24,
		'&:last-child': {
			marginBottom: 0,
		},
	},
	question: {
		padding: 15,
		borderRadius: 5,
		marginBottom: 16,
		backgroundColor: theme.colors.gray200,
	},
	answer: {
		padding: 24,
		borderRadius: 5,
		backgroundColor: theme.colors.white,
		border: `1px solid ${theme.colors.gray300}`,
		'& > h1, & > h2, & > h3, & > h4, & > h5, & > h6, & > p, & > ul, & > ol': {
			marginBottom: 15,
		},
		'& > *:last-child': {
			marginBottom: 0,
		},
	},
	emptyState: {
		padding: 24,
		borderRadius: 5,
		backgroundColor: theme.colors.white,
		border: `1px solid ${theme.colors.gray300}`,
	},
	disclaimer: {
		marginTop: 24,
		marginBottom: 0,
		color: theme.colors.gray700,
		textAlign: 'center',
	},
}));

export const ReportAiConversation = () => {
	const classes = useStyles();
	const { aiConversationId } = useParams<{ aiConversationId?: string }>();
	const [aiConversation, setAiConversation] = useState<AiConversation>();
	const [reportMetadata, setReportMetadata] = useState<ReportAiConversationMetadata>();

	const remarkComponents = useMemo(
		() =>
			getRemarkComponents({
				onSuggestionClick: () => {
					return;
				},
				getAiConversationId: () => aiConversationId ?? '',
				readOnly: true,
			}),
		[aiConversationId]
	);

	const fetchData = useCallback(async () => {
		if (!aiConversationId) {
			throw new Error('aiConversationId is undefined.');
		}

		const response = await aiConversationService.getReportConversation(aiConversationId).fetch();
		setAiConversation(response.aiConversation);
		setReportMetadata(response.reportMetadata);
	}, [aiConversationId]);

	const conversationInitiatedAt = useMemo(() => {
		if (!reportMetadata?.conversationInitiatedAt) {
			return '';
		}

		return format(parseISO(reportMetadata.conversationInitiatedAt), 'MM/dd/yyyy h:mm:ss a');
	}, [reportMetadata?.conversationInitiatedAt]);

	return (
		<AsyncPage fetchData={fetchData}>
			<Helmet>
				<title>Hackett Connect - AskHackett Conversation</title>
			</Helmet>

			<div className={classes.page}>
				<Container>
					<div className={classes.header}>
						<h1 className={classes.title}>{aiConversation?.title || 'AskHackett Conversation'}</h1>
						<p className={classes.subtitle}>
							Read-only conversation view for report consumers.
						</p>
					</div>

					{reportMetadata && (
						<div className={classes.metadataCard}>
							<div className={classes.metadataRow}>
								<div className={classes.metadataItem}>
									<p className={classes.metadataLabel}>Conversation Initiated</p>
									<p className={classes.metadataValue}>{conversationInitiatedAt}</p>
								</div>
							</div>
						</div>
					)}

					<div className={classes.conversation}>
						{(aiConversation?.conversationQueries ?? []).length > 0 ? (
							aiConversation?.conversationQueries.map((conversationQuery) => (
								<div key={conversationQuery.conversationQueryId} className={classes.queryBlock}>
									<div className={classes.question}>{conversationQuery.query}</div>
									<div className={classes.answer}>
										<Markdown
											remarkPlugins={[remarkDirective, remarkCustomTokens]}
											components={remarkComponents}
										>
											{conversationQuery.queryResponse ?? ''}
										</Markdown>
									</div>
								</div>
							))
						) : (
							<div className={classes.emptyState}>
								<p className="mb-0">This conversation does not contain any messages.</p>
							</div>
						)}

						<p className={classes.disclaimer}>AI-generated content may be incomplete or inaccurate.</p>
					</div>
				</Container>
			</div>
		</AsyncPage>
	);
};
